import { DEBUG, IS_TEST, PRODUCTION_HOSTNAME } from '../../config';
import { IS_TAURI } from './globalEnvironment';

export const APP_WAKE_EVENT = 'app-wake';

export function getPlatform() {
  const { userAgent, platform } = window.navigator;

  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  if (
    iosPlatforms.indexOf(platform) !== -1 ||
    // For new IPads with M1 chip and IPadOS platform returns "MacIntel"
    (platform === 'MacIntel' &&
      'maxTouchPoints' in navigator &&
      navigator.maxTouchPoints > 2)
  ) { return 'iOS'; }

  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  if (macosPlatforms.indexOf(platform) !== -1) return 'macOS';

  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  if (windowsPlatforms.indexOf(platform) !== -1) return 'Windows';

  if (/Android/.test(userAgent)) return 'Android';

  if (/Linux/.test(platform)) return 'Linux';

  return undefined;
}

export const IS_PRODUCTION_HOST = window.location.host === PRODUCTION_HOSTNAME;
export const PLATFORM_ENV = getPlatform();
export const IS_MAC_OS = PLATFORM_ENV === 'macOS';
export const IS_WINDOWS = PLATFORM_ENV === 'Windows';
export const IS_LINUX = PLATFORM_ENV === 'Linux';
export const IS_IOS = PLATFORM_ENV === 'iOS';
export const IS_ANDROID = PLATFORM_ENV === 'Android';
export const IS_MOBILE = IS_IOS || IS_ANDROID;
export const IS_CHROMIUM =
  navigator.userAgentData?.brands.some((data) => data.brand === 'Chromium') ||
  /Chrom(e|ium)\//.test(navigator.userAgent);
export const IS_SAFARI =
  !IS_CHROMIUM && /applewebkit/i.test(navigator.userAgent);
export const IS_YA_BROWSER = navigator.userAgent.includes('YaBrowser');
export const IS_FIREFOX =
  navigator.userAgent.toLowerCase().includes('firefox') ||
  navigator.userAgent.toLowerCase().includes('iceweasel') ||
  navigator.userAgent.toLowerCase().includes('icecat');

export const MouseButton = {
  Main: 0,
  Auxiliary: 1,
  Secondary: 2,
  Fourth: 3,
  Fifth: 4,
};

export const IS_PWA =
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone ||
  document.referrer.includes('android-app://');

export const IS_APP = IS_PWA || IS_TAURI;

// The desktop shell saves downloads itself, through `saveToDownloads`, instead of handing them to
// the webview. macOS is where this is mandatory: WKWebView has no download manager at all, so a
// click on an `<a download>` pointing at a `blob:` URL was dropped without a prompt, a file, or an
// error. Windows (WebView2) and Linux (WebKitGTK) go the same way to keep one code path and one
// reported outcome on every platform — and WebKitGTK shares the engine family that broke on macOS.
export const IS_NATIVE_SAVE_REQUIRED = IS_TAURI;

export const IS_TOUCH_ENV = window.matchMedia('(pointer: coarse)').matches;
export const IS_VOICE_RECORDING_SUPPORTED = Boolean(
  window.navigator.mediaDevices &&
  'getUserMedia' in window.navigator.mediaDevices &&
  (window.AudioContext || (window as any).webkitAudioContext),
);
export const IS_EMOJI_SUPPORTED =
  PLATFORM_ENV && (IS_MAC_OS || IS_IOS) && isLastEmojiVersionSupported();

export const IS_SERVICE_WORKER_SUPPORTED = 'serviceWorker' in navigator;

// File System Access folder picker — Chromium only (Chrome/Edge), not Firefox/Safari. Lets a batch
// download write every file into one chosen folder
export const IS_DIRECTORY_PICKER_SUPPORTED = 'showDirectoryPicker' in window;

// TODO Consider failed service worker
// Progressive (range-streamed) media is served by the service worker intercepting `/progressive/`.
// The webpack-dev-server build doesn't control that scope, so streaming fails in dev — fall back to
// the blob/download path there (works without the SW). Production keeps progressive streaming.
export const IS_PROGRESSIVE_SUPPORTED = IS_SERVICE_WORKER_SUPPORTED && !DEBUG;

// Progressive streaming additionally requires the service worker to be actively CONTROLLING this page,
// not merely registered: the `<audio>`/`<video>` src is a `/progressive/` URL only the controlling SW
// can serve. On a cold load the SW registers but doesn't claim the page until `controllerchange`, so
// until then a progressive request falls through to the SPA (returns `index.html`) and the media never
// plays until a manual reload. Checked at call time because control is acquired asynchronously; callers
// fall back to the blob/download path while the SW isn't yet in control.
export function isProgressiveActive(): boolean {
  return (
    IS_PROGRESSIVE_SUPPORTED && Boolean(navigator.serviceWorker?.controller)
  );
}
export const IS_OPUS_SUPPORTED = Boolean(
  new Audio().canPlayType('audio/ogg; codecs=opus'),
);
export const IS_CANVAS_FILTER_SUPPORTED =
  !IS_TEST &&
  'filter' in (document.createElement('canvas').getContext('2d') || {});
export const IS_REQUEST_FULLSCREEN_SUPPORTED =
  'requestFullscreen' in document.createElement('div');
export const ARE_CALLS_SUPPORTED = !IS_FIREFOX;

export const IS_WAVE_TRANSFORM_SUPPORTED =
  !IS_MOBILE &&
  !IS_FIREFOX && // https://bugzilla.mozilla.org/show_bug.cgi?id=1961378
  !IS_SAFARI; // https://bugs.webkit.org/show_bug.cgi?id=245510
export const IS_SNAP_EFFECT_SUPPORTED =
  !IS_MOBILE &&
  !IS_FIREFOX && // https://bugzilla.mozilla.org/show_bug.cgi?id=1896504
  !IS_SAFARI;
export const IS_FLUID_BACKGROUND_SUPPORTED = !IS_FIREFOX;

const TEST_VIDEO = document.createElement('video');

export const IS_WEBM_SUPPORTED = Boolean(
  TEST_VIDEO.canPlayType('video/webm; codecs="vp9"').replace('no', ''),
);

export const MASK_IMAGE_DISABLED = true;
export const IS_OPFS_SUPPORTED = Boolean(navigator.storage?.getDirectory);
if (IS_OPFS_SUPPORTED) {
  // Clear old contents
  (async () => {
    try {
      const directory = await navigator.storage.getDirectory();
      await directory.removeEntry('downloads', { recursive: true });
    } catch {
      // Ignore
    }
  })();
}

export const IS_OFFSET_PATH_SUPPORTED = CSS.supports('offset-rotate: 0deg');
export const IS_BACKDROP_BLUR_SUPPORTED =
  CSS.supports('backdrop-filter: blur()') ||
  CSS.supports('-webkit-backdrop-filter: blur()');
export const IS_INSTALL_PROMPT_SUPPORTED = 'onbeforeinstallprompt' in window;
export const IS_OPEN_IN_NEW_TAB_SUPPORTED = !(IS_PWA && IS_MOBILE);
export const IS_TRANSLATION_SUPPORTED = !IS_TEST;
export const IS_TRANSLATION_DETECTOR_SUPPORTED = 'LanguageDetector' in window;
export const IS_VIEW_TRANSITION_SUPPORTED =
  CSS.supports('view-transition-class: test') && !IS_FIREFOX; // https://bugzilla.mozilla.org/show_bug.cgi?id=1994547
export const IS_WEBAUTHN_SUPPORTED =
  navigator.credentials &&
  window.PublicKeyCredential &&
  'parseCreationOptionsFromJSON' in PublicKeyCredential;

export const MESSAGE_LIST_SENSITIVE_AREA = 750;

export const SCROLLBAR_WIDTH = (() => {
  const el = document.createElement('div');
  el.style.cssText = 'overflow:scroll; visibility:hidden; position:absolute;';
  el.classList.add('custom-scroll');
  document.body.appendChild(el);
  const width = el.offsetWidth - el.clientWidth;
  el.remove();

  document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);

  return width;
})();

export const MAX_BUFFER_SIZE = (IS_MOBILE ? 512 : 2000) * 1024 ** 2; // 512 OR 2000 MB

// Decides whether emoji render as system text or as ~5000 PNG files from `img-apple-64/`. The probe
// used to be an Emoji 17.0 sequence, which no shipping Apple font has yet — so it always failed, and
// every emoji in the app fell back to an `<img>`. Opening the picker then meant hundreds of image
// requests and decodes, which is the difference between our picker and Telegram's (measured: their
// emoji panel contains zero `<img>` elements, ours renders one per cell).
// Probe an emoji the platform realistically has instead. A machine older than this still falls back
// to images, exactly as before; a newer one gets native rendering, which on macOS/iOS — the only
// platforms this path is enabled for — is the very same Apple artwork, minus the downloads.
function isLastEmojiVersionSupported() {
  const ALLOWABLE_CALCULATION_ERROR_SIZE = 5;
  const inlineEl = document.createElement('span');
  inlineEl.classList.add('emoji-test-element');
  document.body.appendChild(inlineEl);

  // A ZWJ sequence from Emoji 15.1. An unsupported sequence fails to compose and renders as two
  // separate glyphs, so it measures about twice as wide as a single emoji.
  inlineEl.innerText = '🙂‍↔️';
  const newEmojiWidth = inlineEl.offsetWidth;
  inlineEl.innerText = '❤️'; // Emoji from 1.0 version
  const legacyEmojiWidth = inlineEl.offsetWidth;

  document.body.removeChild(inlineEl);

  return (
    Math.abs(newEmojiWidth - legacyEmojiWidth) <
      ALLOWABLE_CALCULATION_ERROR_SIZE
  );
}

export const IS_GEOLOCATION_SUPPORTED = 'geolocation' in navigator;

export const getGeolocationStatus = async () => {
  try {
    const permissionStatus = await navigator.permissions.query({
      name: 'geolocation',
    });

    if (
      permissionStatus.state === 'granted' ||
      permissionStatus.state === 'prompt'
    ) {
      const geolocation = await new Promise<GeolocationCoordinates>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve(position.coords),
            (error) => reject(error),
          );
        },
      );
      return { accessRequested: true, accessGranted: true, geolocation };
    }
    if (permissionStatus.state === 'denied') {
      return { accessRequested: true, accessGranted: false };
    }
  } catch (error) {
    return { accessRequested: false, accessGranted: false };
  }

  return { accessRequested: false, accessGranted: false };
};
